import { useRef, useState } from 'react';
import { useErrorNotice } from './useErrorNotice';
import { readFileAndGetCoordinates } from '../app/components/visualization/shoreR/utils';
import {
  MAX_TRANSECT_EXTENT,
  MAX_TRANSECT_STEP,
  MIN_TRANSECT_EXTENT,
  MIN_TRANSECT_STEP,
  TRANSECT_CONFIG,
} from '../common/utils';
import * as turf from '@turf/turf';
import ee from '../services/earth-engine';
import { generateOrthogonalTransects } from '../algorithms/shoreline';

export const useTransectsConfig = ({ baseLineCoords, mapRef, roiCoords, drawingManager, t, onConfirmed }) => {
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const [transectsStep, setTransectsStep] = useState(400);
  const [transectsExtent, setTransectsExtent] = useState(1600);
  const [transectsObjects, setTransectsObjects] = useState([]);
  const [threshold, setThreshold] = useState(0.01);
  const [confirmTransectsConfigEnabled, setConfirmTransectsConfigEnabled] = useState(true);

  /**
   * The preview that is still being worked out, if any. Drawing one is a round trip to Earth Engine
   * and the lines appear in its callback, so a preview asked for a moment ago is still on its way
   * while the user moves on - and lands afterwards, drawing itself over whatever has happened since.
   * Keeping the run here is what lets a new one, or the end of the step, call off the old one.
   */
  const previewRunRef = useRef(null);

  /**
   * The preview lines that are on the map right now. Kept alongside the state because the clean-up
   * runs from an effect that fires on the step changing: the list it closed over is the one from
   * the render it belongs to, and a preview that finished drawing a moment earlier would not be in
   * it. The ref is written on every render, so it is always the current one.
   */
  const drawnPreviewRef = useRef([]);
  drawnPreviewRef.current = transectsObjects;

  /**
   * @function cancelPendingPreview
   * @description Disowns a preview that has been asked for but not drawn yet, so its answer is
   * thrown away when it arrives.
   * **Called by:
   * @see {generateTransectsPreview} - a newer preview supersedes the one in flight.
   * @see {clearTransectsPreview} - the step is over and no preview should land at all.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const cancelPendingPreview = () => {
    if (previewRunRef.current) previewRunRef.current.cancelled = true;
    previewRunRef.current = null;
  };

  /**
   * @function generateTransectsPreview
   * @description Generates and draws a live preview of the orthogonal transects on the map,
   * based on the current step/extent settings and the drawn baseline.
   * 1. Validates the step/extent values against their min/max bounds.
   * 2. Clears any previously drawn preview transects from the map.
   * 3. Generates the orthogonal transects for the current baseline and evaluates them.
   * 4. Draws each transect as a Google Maps Polyline and stores the references for later cleanup.
   * @see {@link generateOrthogonalTransects} - computes the transect geometries.
   * **Called by:
   * @see {CoastlineAnalysisSteps} - via the step/extent inputs' onChange/onBlur.
   * @memberof module:Shoreline
   * @returns {Function|void} A cleanup function that cancels a pending evaluate() callback, when one was scheduled.
   */
  const generateTransectsPreview = () => {
    if (!transectsExtent || !transectsStep) return;
    const isInvalid =
      transectsExtent < MIN_TRANSECT_EXTENT ||
      transectsExtent > MAX_TRANSECT_EXTENT ||
      transectsStep < MIN_TRANSECT_STEP ||
      transectsStep > MAX_TRANSECT_STEP;
    setConfirmTransectsConfigEnabled(!isInvalid);
    if (baseLineCoords.length < 2) return;
    drawnPreviewRef.current.forEach((object) => object.setMap(null));
    drawnPreviewRef.current = [];
    setTransectsObjects([]);
    if (isInvalid) return;
    const eeTransects = generateOrthogonalTransects(baseLineCoords, transectsStep, transectsExtent);
    cancelPendingPreview();
    const run = { cancelled: false };
    previewRunRef.current = run;
    ee.List(eeTransects).evaluate((transectList, error) => {
      if (run.cancelled) return;
      if (error) return console.error('Error evaluating transects', error);
      let tempTransectsObjects = [];
      transectList.forEach((object) => {
        const coords = object.geometry.coordinates;
        const path = coords.map((arr) => ({
          lat: arr[1],
          lng: arr[0],
        }));
        const polyline = new window.google.maps.Polyline({
          path,
          transect: object,
          ...TRANSECT_CONFIG.POLYLINE_STYLE,
        });
        polyline.setMap(mapRef.current);
        tempTransectsObjects.push(polyline);
      });
      setTransectsObjects(tempTransectsObjects);
      previewRunRef.current = null;
    });
    return () => {
      run.cancelled = true;
    };
  };

  /**
   * @function clearTransectsPreview
   * @description Takes the preview transects off the map.
   * They are drawn in purple while the spacing and length are being adjusted, to show where the
   * transects would fall. They are not results and are not in the legend - so once the analysis has
   * drawn the real ones, in the colours the legend explains, the purple ones left underneath read as
   * a fifth, unexplained kind of transect.
   * Taking them off the map is not enough on its own: the last one is usually still being worked
   * out when the user confirms - the field's own blur asks for it, and the confirm click follows
   * immediately - so the request is called off as well, or it lands mid-analysis and draws itself.
   * **Called by:
   * @see {AnalysisSteps} - the moment the configuration is confirmed and the analysis begins.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const clearTransectsPreview = () => {
    cancelPendingPreview();
    drawnPreviewRef.current.forEach((object) => object.setMap && object.setMap(null));
    drawnPreviewRef.current = [];
    setTransectsObjects([]);
  };

  /**
   * @function importStepExtentTransects
   * @description
   * Imports transect coordinates from a KML file and calculates the extension and step distances.
   * 1. Get distance of each transect to determine the most common extension length
   * 2. Calculate midpoints of each transect to determine spacing (step) between them
   * 3. Sets the calculated extension and step values in state
   * @param {Array} allTransects - Array of transect coordinates imported from a KML file.
   * **Called by:
   * @see {readTransectsFileData} - This function calls `importStepExtentTransects` after reading transect data from a file.
   * @memberof module:Shoreline
   * @returns {void}
   *   */
  const importStepExtentTransects = (allTransects) => {
    if (!allTransects || allTransects.length === 0) {
      notifyError(t('forms.csqueeze.2.errorMessageNoValidCoord'));
      return;
    }
    const getMajority = (array) => {
      const count = array.reduce((acc, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      }, {});
      return Number(Object.keys(count).reduce((a, b) => (count[a] > count[b] ? a : b)));
    };
    let extensions = [];
    allTransects?.forEach((transect) => {
      extensions.push(
        turf.distance(turf.point(transect[0]), turf.point(transect[1]), {
          units: 'meters',
        }),
      );
    });
    const extension = Math.round(getMajority(extensions));
    const steps = [];
    const center_transects = allTransects?.map((transect) => {
      const coords_lng = (transect[0][0] + transect[1][0]) / 2;
      const coords_lat = (transect[0][1] + transect[1][1]) / 2;
      return [coords_lng, coords_lat];
    });
    if (center_transects.length < 2) {
      notifyError(t('forms.shorelineAnalysis.transectsSettings.alert.importTransectQuantity'));
      return;
    }
    for (let i = 0; i < center_transects.length - 1; i++) {
      const dist = turf.distance(center_transects[i], center_transects[i + 1], {
        units: 'meters',
      });
      steps.push(dist);
    }
    const step = Math.round(getMajority(steps));
    setTransectsExtent(extension);
    setTransectsStep(step);
  };

  /**
   * @function readTransectsFileData
   * @description
   * Reads a KML file containing transect coordinates, parses the coordinates,
   * and imports them for analysis.
   * @param {Object} e - The file input change event containing the selected file.
   * @see {@link readFileAndGetCoordinates} - Called to parse KML file content and extract coordinates.
   * @see {@link importStepExtentTransects} - Called to process and import the transect coordinates.
   * **Called by:
   * @see {CoastlineAnalysisSteps} - via the transects KML file input's onChange.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const readTransectsFileData = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    try {
      reader.onload = async (event) => {
        const allTransects = await readFileAndGetCoordinates(reader.result, t, notifyError);
        if (allTransects?.length === 0) {
          notifyError(t('forms.csqueeze.2.errorMessageNoValidCoord'));
          return;
        }
        importStepExtentTransects(allTransects);
      };
      if (file) reader.readAsText(file);
    } catch (error) {
      console.error('Error reading transects file:', error);
      notifyError(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
    }
  };

  return {
    generateTransectsPreview,
    clearTransectsPreview,
    importStepExtentTransects,
    readTransectsFileData,
    transectsExtent,
    setTransectsExtent,
    transectsStep,
    setTransectsStep,
    transectsObjects,
    setTransectsObjects,
    threshold,
    setThreshold,
    confirmTransectsConfigEnabled,
  };
};
